<template>
  <div id="App" v-cloak>
    <InfoPanel v-if="infoPanelShowing" />
    <StatsPanel v-if="statsPanelShowing" />      
      
      <Header />
      <main class="container">
        <Flag v-if="answer" :country="answer" />
        <GuessAnswer :guess="guesses[index-1] || null" v-for="index in this.allowedGuessCount" :key="index" />
        <GuessInput />
        <span @click="copySeedValue()" v-if="gameMode == 'seed' && seedText" class="seed-value">Seed: {{seedText}}</span>
        <span class="game-state" v-else>#{{getDaysSinceStart()}}</span>
        <DebugMenu v-if="showDebugMenu" />
      </main>
  </div>
</template>

<script>
    import { mapGetters, mapState, mapActions } from 'vuex';

    import GuessAnswer from './components/GuessAnswer.vue';
    import GuessInput from './components/GuessInput.vue';
    import Flag from './components/Flag.vue';
    import Header from './components/Header.vue';
    import DebugMenu from './components/DebugMenu.vue';
    import InfoPanel from './components/InfoPanel.vue';
    import StatsPanel from './components/StatsPanel.vue';

    import { getTodaysLocation, generateSeedText, generateDailySeedText } from './lib/daily';
    import { daysSinceStart, getTodaysDateString} from './lib/helpers';
    import { loadGuessesData } from './lib/guess';

export default {
	data() {
		return {
            GuessArr: new Array(this.allowedGuessCount).fill(undefined),
		};
	},
	components: {
        GuessAnswer,
        GuessInput,
        Flag,
        Header,
        DebugMenu,
        InfoPanel,
        StatsPanel,
	},
	methods: {
        ...mapActions('UIManager', [
            'toggleDebugMenu',
        ]),

        ...mapActions('GameManager', [
            'setAnswerLocationByName',
            'setSeedText',
            'setGameMode',
            'setTodayGuesses',
        ]),

        ...mapActions('StatsManager', [
            'setGuessHistory',
            'updateStatsBasedOnHistory',
        ]),

        copySeedValue() {
            const url = `${window.location.origin + window.location.pathname}?seed=${this.seedText}`
            try {
                navigator.clipboard.writeText(url);
            } catch($e) {
                console.warn("Couldnt copy to clipboard because", $e);
            }
        },

        getDaysSinceStart() {
           return daysSinceStart();
        },

        loadStatHistory() {
            const guessData = loadGuessesData();
            const today = getTodaysDateString();

            const todayGuesses = guessData.filter(guess => {
                // If there is a seed text it needs to match todays guesses
                const hasSeedTextAndMatches = (this.seedText !== null && guess.seedText === this.seedText);
                return (guess.date === today &&
                        ((this.gameMode === 'seed' && guess.gameMode === 'seed' && hasSeedTextAndMatches) || 
                        (this.gameMode === 'daily' && guess.gameMode === "daily")));
            });

            this.setGuessHistory(guessData);
            this.setTodayGuesses(todayGuesses);
            this.updateStatsBasedOnHistory();
        }
	},
    computed: {
        ...mapGetters('GameManager', [
            'getGuessCount',
        ]),
        ...mapState('GameManager', [
            'guesses',
            'answer',
            'allowedGuessCount',
            'seedText',
            'gameMode',
        ]),
        ...mapState('UIManager', [
            'infoPanelShowing',
            'statsPanelShowing',
            'showDebugMenu',
        ]),
    },
    mounted() {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(urlSearchParams.entries());

        if (typeof params.debug !== 'undefined') {
            this.toggleDebugMenu(true)
        }
        else {
            this.toggleDebugMenu(false);
        }

        let seedText = '';
        let seedInput = '';
        if (typeof params.seed !== 'undefined') {
            if (params.seed === 'random') {
                seedInput = Math.random().toString().slice(2);
                seedText = generateSeedText(seedInput);
            }
            else { 
                seedInput = params.seed;
                seedText = generateSeedText(seedInput);
            }

            this.setGameMode('seed');
        }
        else {
            seedText = generateDailySeedText();
            this.setGameMode('daily');
        }

        this.setSeedText(seedInput);
        this.setAnswerLocationByName(getTodaysLocation(seedText).name);

        this.loadStatHistory();
    },

    filters: {
        seedCleanup(val) {
            return val.toString().slice(2);
        },
    }
};
</script>

<style lang="scss">
    .flag-container {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        max-width: 1240px;
        margin-left: auto;
        margin-right: auto;
        margin-top: 2rem;
        margin-bottom: 40px;
    }

    .game-ui {
        max-width: 300px;
        margin-left: auto;
        margin-right: auto;
        text-align: center;

        &__button {
            border: none;
            appearance: none;
            padding: 11px 20px;
            background-color: #23494e;
            color: #fff;
            font-weight: 600;
            font-size: 19px;
            line-height: 1.3;
            cursor: pointer;
            margin-bottom: 25px;
            min-width: 9rem;

            &.is-selected {
                color: #23494e;
                background-color: transparent;
                border: 2px solid #23494e;
            }
        }
    }

    .seed-value {
        cursor: pointer;
    }

    .game-state, 
    .seed-value {
        font-size: 12px;
        color: #898989;
        margin-top: 5px;
        display: inline-block;
    }
</style>

<style lang="scss">
/*! normalize.css v8.0.1 | MIT License | github.com/necolas/normalize.css */

/* Document
   ========================================================================== */

/**
 * 1. Correct the line height in all browsers.
 * 2. Prevent adjustments of font size after orientation changes in iOS.
 */

html {
    line-height: 1.15;
    /* 1 */
    -webkit-text-size-adjust: 100%;
    /* 2 */
}

/* Sections
   ========================================================================== */

/**
 * Remove the margin in all browsers.
 */

body {
    margin: 0;
}

/**
 * Render the `main` element consistently in IE.
 */

main {
    display: block;
}

/**
 * Correct the font size and margin on `h1` elements within `section` and
 * `article` contexts in Chrome, Firefox, and Safari.
 */

h1 {
    font-size: 2em;
    margin: 0.67em 0;
}

/* Grouping content
   ========================================================================== */

/**
 * 1. Add the correct box sizing in Firefox.
 * 2. Show the overflow in Edge and IE.
 */

hr {
    box-sizing: content-box;
    /* 1 */
    height: 0;
    /* 1 */
    overflow: visible;
    /* 2 */
}

/**
 * 1. Correct the inheritance and scaling of font size in all browsers.
 * 2. Correct the odd `em` font sizing in all browsers.
 */

pre {
    font-family: monospace, monospace;
    /* 1 */
    font-size: 1em;
    /* 2 */
}

/* Text-level semantics
   ========================================================================== */

/**
 * Remove the gray background on active links in IE 10.
 */

a {
    background-color: transparent;
}

/**
 * 1. Remove the bottom border in Chrome 57-
 * 2. Add the correct text decoration in Chrome, Edge, IE, Opera, and Safari.
 */

abbr[title] {
    border-bottom: none;
    /* 1 */
    text-decoration: underline;
    /* 2 */
    text-decoration: underline dotted;
    /* 2 */
}

/**
 * Add the correct font weight in Chrome, Edge, and Safari.
 */

b,
strong {
    font-weight: bolder;
}

/**
 * 1. Correct the inheritance and scaling of font size in all browsers.
 * 2. Correct the odd `em` font sizing in all browsers.
 */

code,
kbd,
samp {
    font-family: monospace, monospace;
    /* 1 */
    font-size: 1em;
    /* 2 */
}

/**
 * Add the correct font size in all browsers.
 */

small {
    font-size: 80%;
}

/**
 * Prevent `sub` and `sup` elements from affecting the line height in
 * all browsers.
 */

sub,
sup {
    font-size: 75%;
    line-height: 0;
    position: relative;
    vertical-align: baseline;
}

sub {
    bottom: -0.25em;
}

sup {
    top: -0.5em;
}

/* Embedded content
   ========================================================================== */

/**
 * Remove the border on images inside links in IE 10.
 */

img {
    border-style: none;
}

/* Forms
   ========================================================================== */

/**
 * 1. Change the font styles in all browsers.
 * 2. Remove the margin in Firefox and Safari.
 */

button,
input,
optgroup,
select,
textarea {
    font-family: inherit;
    /* 1 */
    font-size: 100%;
    /* 1 */
    line-height: 1.15;
    /* 1 */
    margin: 0;
    /* 2 */
}

/**
 * Show the overflow in IE.
 * 1. Show the overflow in Edge.
 */

button,
input {
    /* 1 */
    overflow: visible;
}

/**
 * Remove the inheritance of text transform in Edge, Firefox, and IE.
 * 1. Remove the inheritance of text transform in Firefox.
 */

button,
select {
    /* 1 */
    text-transform: none;
}

/**
 * Correct the inability to style clickable types in iOS and Safari.
 */

button,
[type="button"],
[type="reset"],
[type="submit"] {
    -webkit-appearance: button;
}

/**
 * Remove the inner border and padding in Firefox.
 */

button::-moz-focus-inner,
[type="button"]::-moz-focus-inner,
[type="reset"]::-moz-focus-inner,
[type="submit"]::-moz-focus-inner {
    border-style: none;
    padding: 0;
}

/**
 * Restore the focus styles unset by the previous rule.
 */

button:-moz-focusring,
[type="button"]:-moz-focusring,
[type="reset"]:-moz-focusring,
[type="submit"]:-moz-focusring {
    outline: 1px dotted ButtonText;
}

/**
 * Correct the padding in Firefox.
 */

fieldset {
    padding: 0.35em 0.75em 0.625em;
}

/**
 * 1. Correct the text wrapping in Edge and IE.
 * 2. Correct the color inheritance from `fieldset` elements in IE.
 * 3. Remove the padding so developers are not caught out when they zero out
 *    `fieldset` elements in all browsers.
 */

legend {
    box-sizing: border-box;
    /* 1 */
    color: inherit;
    /* 2 */
    display: table;
    /* 1 */
    max-width: 100%;
    /* 1 */
    padding: 0;
    /* 3 */
    white-space: normal;
    /* 1 */
}

/**
 * Add the correct vertical alignment in Chrome, Firefox, and Opera.
 */

progress {
    vertical-align: baseline;
}

/**
 * Remove the default vertical scrollbar in IE 10+.
 */

textarea {
    overflow: auto;
}

/**
 * 1. Add the correct box sizing in IE 10.
 * 2. Remove the padding in IE 10.
 */

[type="checkbox"],
[type="radio"] {
    box-sizing: border-box;
    /* 1 */
    padding: 0;
    /* 2 */
}

/**
 * Correct the cursor style of increment and decrement buttons in Chrome.
 */

[type="number"]::-webkit-inner-spin-button,
[type="number"]::-webkit-outer-spin-button {
    height: auto;
}

/**
 * 1. Correct the odd appearance in Chrome and Safari.
 * 2. Correct the outline style in Safari.
 */

[type="search"] {
    -webkit-appearance: textfield;
    /* 1 */
    outline-offset: -2px;
    /* 2 */
}

/**
 * Remove the inner padding in Chrome and Safari on macOS.
 */

[type="search"]::-webkit-search-decoration {
    -webkit-appearance: none;
}

/**
 * 1. Correct the inability to style clickable types in iOS and Safari.
 * 2. Change font properties to `inherit` in Safari.
 */

::-webkit-file-upload-button {
    -webkit-appearance: button;
    /* 1 */
    font: inherit;
    /* 2 */
}

/* Interactive
   ========================================================================== */

/*
 * Add the correct display in Edge, IE 10+, and Firefox.
 */

details {
    display: block;
}

/*
 * Add the correct display in all browsers.
 */

summary {
    display: list-item;
}

/* Misc
   ========================================================================== */

/**
 * Add the correct display in IE 10+.
 */

template {
    display: none;
}

/**
 * Add the correct display in IE 10.
 */

[hidden] {
    display: none;
}

    *, *:before, *:after {
        box-sizing: border-box;
    }

    :focus-visible {
        outline: 2px solid #ba8af4 !important;
    }

    .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        -webkit-clip-path: inset(50%);
        clip-path: inset(50%);
        border: 0;
    }

    html {
        background-color: #211f2e;
        font-family: 'Poppins', sans-serif;
    }

    body {
        font-size: 1rem;
        font-weight: 400;
        line-height: 1.5;
        text-align: left;
    }

    p {
        margin-top: 0;
        line-height: 1.2;
        margin-bottom: 1.75rem;
    }

    h1, h2, h3, h4, h5 {
        margin-top: 0;
        margin-bottom: .25rem;
        line-height: 1.2;
    }

    input {
        width: 100%;
        border: 3px solid grey;
        padding: 3px 10px;
    }
    

    .container {
        max-width: 500px;
        margin: 0 auto;
        padding: 0 15px;
    }

    .modal {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        top: 0;
        background-color: #211f2e;
        z-index: 100;
        color: white;
        padding: 1rem 0;
        font-size: 14px;

        &__header {
            display: flex;
            justify-content: space-between;
            vertical-align: top;
            align-items: center;
        }

        p, p a {
            color: #d3d3d3;
        }

        p a {
            font-weight: bold;
        }
    
        button {
            height: 33px;
            width: 33px;
            margin: 5px;
            appearance: none;
            background: none;
            color: white;
            padding: 2px;
            border: none;
            cursor: pointer;

            svg path {
                fill: #ba8af5;
                will-change: fill;
                transition: fill 0.2s ease-in-out;
            }

            &:hover,
            &:focus {
                svg path {
                    fill: #5c2aa1;
                }
            }
        }

    }

    ul.plain {
        list-style: none;
        padding-left: 0;
        margin: .5rem 0 2rem;

        li {
            list-style: none;
            margin-bottom: 10px;
        }
    }
</style>